/* RESPONSIBLE TEAM: team-phone */
import type { RTCSample } from '@twilio/voice-sdk';
import { post } from 'embercom/lib/ajax';

interface CallQualityDeps {
  appId: string;
  adminId: number;
  getConnectionType: () => string | null;
  getEdge: () => string | undefined;
}

export default class CallQualityTracker {
  private deps: CallQualityDeps;
  private samples: RTCSample[] = [];
  private warnings: Array<{ name: string; timestamp: number }> = [];
  private errors: Array<{ code: number; message: string; timestamp: number }> = [];
  private callStartAt?: number;
  private codecHistory: string[] = [];
  private callSid?: string;
  private direction?: 'inbound' | 'outbound';
  private conversationId?: number;

  constructor(deps: CallQualityDeps) {
    this.deps = deps;
  }

  startTracking(params: {
    direction: 'inbound' | 'outbound';
    callSid?: string;
    conversationId?: number;
  }): void {
    this.direction = params.direction;
    this.callSid = params.callSid;
    this.conversationId = params.conversationId;
  }

  onAccept(callSid?: string): void {
    this.callStartAt = Date.now();
    this.callSid = callSid;
  }

  onSample(sample: RTCSample): void {
    this.samples.push(sample);
    let codec = sample.codecName;
    if (codec && this.codecHistory[this.codecHistory.length - 1] !== codec) {
      this.codecHistory.push(codec);
    }
  }

  onWarning(warningName: string): void {
    this.warnings.push({ name: warningName, timestamp: Date.now() });
  }

  onError(error: { code: number; message: string }): void {
    this.errors.push({ code: error.code, message: error.message, timestamp: Date.now() });
  }

  onDisconnect(): void {
    this.sendDiagnostics();
  }

  private sendDiagnostics(): void {
    try {
      let samples = this.samples;
      if (samples.length === 0) {
        return;
      }

      let payload = this.aggregateSamples(samples);

      // Fire-and-forget POST
      post('/ember/twilio_call_quality_diagnostics', {
        app_id: this.deps.appId,
        admin_id: this.deps.adminId,
        ...payload,
      }).catch((_e: unknown) => {
        // Never block disconnect flow
      });
    } catch (e: unknown) {
      // Never block disconnect flow
    } finally {
      this.samples = [];
      this.warnings = [];
      this.errors = [];
      this.callStartAt = undefined;
      this.codecHistory = [];
      this.callSid = undefined;
      this.direction = undefined;
      this.conversationId = undefined;
    }
  }

  private aggregateSamples(samples: RTCSample[]): Record<string, any> {
    let totalSamples = samples.length;
    let durationSeconds = this.callStartAt
      ? Math.round((Date.now() - this.callStartAt) / 1000)
      : totalSamples;

    // MOS stats
    let mosValues = samples.map((s) => s.mos).filter((m): m is number => m !== null);
    let mosAvg = mosValues.length > 0 ? mosValues.reduce((a, b) => a + b, 0) / mosValues.length : 0;
    let mosMin = mosValues.length > 0 ? Math.min(...mosValues) : 0;
    let mosMax = mosValues.length > 0 ? Math.max(...mosValues) : 0;
    let mosStdev = this.calculateStdev(mosValues, mosAvg);
    let mosSecondsBelow3 = mosValues.filter((m) => m < 3.0).length;
    let mosSecondsBelow2 = mosValues.filter((m) => m < 2.0).length;
    let mosPercentBelow3 =
      mosValues.length > 0 ? this.round2((mosSecondsBelow3 / mosValues.length) * 100) : 0;

    // Jitter stats
    let jitterValues = samples.map((s) => s.jitter);
    let jitterAvg = jitterValues.reduce((a, b) => a + b, 0) / jitterValues.length;
    let jitterMax = Math.max(...jitterValues);
    let jitterP95 = this.percentile(jitterValues, 95);

    // RTT stats
    let rttValues = samples.map((s) => s.rtt);
    let rttAvg = rttValues.reduce((a, b) => a + b, 0) / rttValues.length;
    let rttMax = Math.max(...rttValues);
    let rttP95 = this.percentile(rttValues, 95);

    // Packet loss
    let lastSample = samples[samples.length - 1];
    let packetsSent = lastSample.totals.packetsSent;
    let packetsLost = lastSample.totals.packetsLost;
    let packetsReceived = lastSample.totals.packetsReceived;
    let packetsLostReceived = lastSample.totals.packetsLostFraction;
    let packetLossPercent = packetsSent > 0 ? this.round4((packetsLost / packetsSent) * 100) : 0;
    let lossFractions = samples.map((s) => s.packetsLostFraction);
    let packetLossMaxFraction = Math.max(...lossFractions);
    let packetLossAvgFraction = lossFractions.reduce((a, b) => a + b, 0) / lossFractions.length;

    // Audio levels
    let inputLevels = samples.map((s) => s.audioInputLevel);
    let outputLevels = samples.map((s) => s.audioOutputLevel);
    let audioInputLevelAvg = Math.round(
      inputLevels.reduce((a, b) => a + b, 0) / inputLevels.length,
    );
    let audioOutputLevelAvg = Math.round(
      outputLevels.reduce((a, b) => a + b, 0) / outputLevels.length,
    );

    // Silent seconds detection (level < 100 ~= silence)
    let silentInputSeconds = inputLevels.filter((l) => l < 100).length;
    let silentInputPercent =
      totalSamples > 0 ? this.round2((silentInputSeconds / totalSamples) * 100) : 0;
    let silentOutputSeconds = outputLevels.filter((l) => l < 100).length;
    let silentOutputPercent =
      totalSamples > 0 ? this.round2((silentOutputSeconds / totalSamples) * 100) : 0;

    // Bandwidth
    let bytesSent = lastSample.totals.bytesSent;
    let bytesReceived = lastSample.totals.bytesReceived;

    // One-way audio detection: mic capturing (audioInputLevel > 100) but bytes not incrementing
    let oneWayAudioSuspectedSeconds = 0;
    for (let i = 1; i < samples.length; i++) {
      let prevBytesSent = samples[i - 1].totals.bytesSent;
      let currBytesSent = samples[i].totals.bytesSent;
      let hasInput = samples[i].audioInputLevel > 100;
      let bytesStalled = currBytesSent === prevBytesSent;
      if (hasInput && bytesStalled) {
        oneWayAudioSuspectedSeconds++;
      }
    }

    // Codec
    let codec = lastSample.codecName;
    let codecChanged = this.codecHistory.length > 1;

    // Degradation periods
    let degradationPeriods = this.findDegradationPeriods(mosValues);

    // Worst 5 samples by MOS
    let worstSamples = [...samples]
      .filter((s) => s.mos !== null)
      .sort((a, b) => (a.mos ?? 5) - (b.mos ?? 5))
      .slice(0, 5)
      .map((s, _i) => ({
        second: samples.indexOf(s),
        mos: s.mos,
        jitter: this.round2(s.jitter),
        rtt: this.round2(s.rtt),
        packet_loss_fraction: this.round4(s.packetsLostFraction),
        audio_input_level: s.audioInputLevel,
        audio_output_level: s.audioOutputLevel,
      }));

    // Warnings (cap at 50)
    let warnings = this.warnings.slice(0, 50);

    return {
      call_sid: this.callSid,
      direction: this.direction,
      conversation_id: this.conversationId,
      duration_seconds: durationSeconds,
      total_samples: totalSamples,
      mos_average: this.round2(mosAvg),
      mos_min: this.round2(mosMin),
      mos_max: this.round2(mosMax),
      mos_stdev: this.round2(mosStdev),
      mos_seconds_below_3: mosSecondsBelow3,
      mos_seconds_below_2: mosSecondsBelow2,
      mos_percent_below_3: mosPercentBelow3,
      jitter_avg_ms: this.round2(jitterAvg),
      jitter_max_ms: this.round2(jitterMax),
      jitter_p95_ms: this.round2(jitterP95),
      rtt_avg_ms: this.round2(rttAvg),
      rtt_max_ms: this.round2(rttMax),
      rtt_p95_ms: this.round2(rttP95),
      packets_sent: packetsSent,
      packets_lost: packetsLost,
      packets_received: packetsReceived,
      packets_lost_received: packetsLostReceived,
      packet_loss_percent: packetLossPercent,
      packet_loss_max_fraction: this.round4(packetLossMaxFraction),
      packet_loss_avg_fraction: this.round4(packetLossAvgFraction),
      audio_input_level_avg: audioInputLevelAvg,
      audio_output_level_avg: audioOutputLevelAvg,
      silent_input_seconds: silentInputSeconds,
      silent_input_percent: silentInputPercent,
      silent_output_seconds: silentOutputSeconds,
      silent_output_percent: silentOutputPercent,
      bytes_sent: bytesSent,
      bytes_received: bytesReceived,
      codec,
      codec_changed: codecChanged,
      codec_history: this.codecHistory,
      one_way_audio_suspected_seconds: oneWayAudioSuspectedSeconds,
      network_online: navigator.onLine,
      connection_type: this.deps.getConnectionType(),
      twilio_edge: this.deps.getEdge(),
      tab_hidden: document.hidden,
      degradation_periods: degradationPeriods.length > 0 ? degradationPeriods : undefined,
      worst_samples: worstSamples.length > 0 ? worstSamples : undefined,
      warnings: warnings.length > 0 ? warnings : undefined,
      warning_count: this.warnings.length,
      errors: this.errors.length > 0 ? this.errors.slice(0, 10) : undefined,
      error_count: this.errors.length,
    };
  }

  private percentile(values: number[], p: number): number {
    let sorted = [...values].sort((a, b) => a - b);
    let index = Math.ceil((p / 100) * sorted.length) - 1;
    return sorted[Math.max(0, index)];
  }

  private calculateStdev(values: number[], mean: number): number {
    if (values.length < 2) {
      return 0;
    }
    let squaredDiffs = values.map((v) => (v - mean) ** 2);
    let avgSquaredDiff = squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
    return Math.sqrt(avgSquaredDiff);
  }

  private round2(value: number): number {
    return Math.round(value * 100) / 100;
  }

  private round4(value: number): number {
    return Math.round(value * 10000) / 10000;
  }

  private findDegradationPeriods(mosValues: number[]): Array<Record<string, number>> {
    let periods: Array<{ startSecond: number; endSecond: number; values: number[] }> = [];
    let currentPeriod: { startSecond: number; values: number[] } | null = null;

    mosValues.forEach((mos, i) => {
      if (mos < 3.0) {
        if (!currentPeriod) {
          currentPeriod = { startSecond: i, values: [mos] };
        } else {
          currentPeriod.values.push(mos);
        }
      } else {
        if (currentPeriod && currentPeriod.values.length >= 3) {
          periods.push({
            startSecond: currentPeriod.startSecond,
            endSecond: i - 1,
            values: currentPeriod.values,
          });
        }
        currentPeriod = null;
      }
    });

    // Handle period that extends to end of call
    if (currentPeriod) {
      let cp = currentPeriod as { startSecond: number; values: number[] };
      if (cp.values.length >= 3) {
        periods.push({
          startSecond: cp.startSecond,
          endSecond: mosValues.length - 1,
          values: cp.values,
        });
      }
    }

    // Return top 5 longest periods, summarized
    return periods
      .sort((a, b) => b.values.length - a.values.length)
      .slice(0, 5)
      .map((p) => this.summarizePeriod(p));
  }

  private summarizePeriod(period: {
    startSecond: number;
    endSecond: number;
    values: number[];
  }): Record<string, number> {
    let avg = period.values.reduce((a, b) => a + b, 0) / period.values.length;
    return {
      start_second: period.startSecond,
      end_second: period.endSecond,
      duration_seconds: period.values.length,
      avg_mos: this.round2(avg),
    };
  }
}
